import type { GameDto } from '@brig/api-types';
import { Cartouche } from '@brig/theme/components';
import { RhumbLines } from '@brig/theme/ornaments';
import { getNation } from '@brig/nations';
import { button, paper, visuallyHidden } from '@brig/theme/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLive } from '../api/hooks.ts';
import { NationFlag } from '../nations/NationFlag.tsx';
import * as styles from '../pages/Invite.css.ts';
import { currentLocale } from '../i18n/index.ts';
import { useDocumentTitle } from '../useDocumentTitle.ts';

const nationName = (id: string) => getNation(id).names[currentLocale()];

/** The link that brings the second player in. */
const inviteLink = (code: string) => `${window.location.origin}/join/${code}`;

/**
 * An online game nobody has joined yet: the invite link to send, until someone takes it. The
 * game's news comes over `/ws`; `onJoined` reads the game again, and it opens.
 */
export function AwaitingOpponent({ game, onJoined }: { game: GameDto; onJoined: () => void }) {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('invite.waiting.pageTitle'));
  const live = useLive();
  const [copied, setCopied] = useState(false);
  useEffect(
    () =>
      live.watch(game.id, (version) => {
        if (version > game.version) onJoined();
      }),
    [game.id, game.version, live, onJoined],
  );
  const link = game.invite ? inviteLink(game.invite.code) : null;
  const expires = game.invite
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(game.invite.expiresAt))
    : '';
  return (
    <main className={styles.sea}>
      <RhumbLines originX={0.5} originY={0.5} opacity={0.12} />
      <section className={`${paper} ${styles.sheet}`}>
        <Cartouche kicker={t('invite.waiting.kicker')}>{t('invite.waiting.title')}</Cartouche>
        <Sides nations={game.nations} />
        {link ? (
          <>
            <p className={styles.body}>{t('invite.waiting.body')}</p>
            <div className={styles.link}>
              <input
                className={styles.linkField}
                value={link}
                readOnly
                aria-label={t('invite.waiting.linkLabel')}
                onFocus={(e) => {
                  e.currentTarget.select();
                }}
              />
              <button
                type="button"
                className={button({ tone: 'seal', size: 'sm' })}
                onClick={() => {
                  void navigator.clipboard.writeText(link).then(() => {
                    setCopied(true);
                  });
                }}
              >
                {copied ? t('invite.waiting.copied') : t('invite.waiting.copy')}
              </button>
            </div>
            <p className={styles.soft} role="status" aria-live="polite">
              {t('invite.waiting.expires', { date: expires })}
            </p>
          </>
        ) : (
          <p className={styles.body}>{t('invite.waiting.notYours')}</p>
        )}
        <Link to="/logbook">{t('game.load.back')}</Link>
      </section>
    </main>
  );
}

/** The inviter's nation (red) against the one the invitee commands (blue). */
export function Sides({ nations }: { nations: GameDto['nations'] }) {
  const { t } = useTranslation();
  return (
    <p className={styles.sides}>
      <span className={styles.side}>
        <NationFlag nation={nations.red} height={20} decorative />
        {nationName(nations.red)}
      </span>
      <span aria-hidden="true">⚔</span>
      <span className={visuallyHidden}>{t('invite.against')}</span>
      <span className={styles.side}>
        <NationFlag nation={nations.blue} height={20} decorative />
        {nationName(nations.blue)}
      </span>
    </p>
  );
}
